"use client";
// ============================================================
// CyberAlem AI Advisor — анимациялы чат (OpenRouter SSE-стрим)
// Кеңесші: шабуыл (Red) + қорғаныс (Blue) — оқу мақсатында.
// ============================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, ImagePlus, RotateCcw, Send, ShieldAlert, Swords, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui";
// локал режим: токен localStorage-де (auth provider осы кілмен сақтайды)
function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("cyberalem.local.token");
}

interface ChatMsg {
  role: "user" | "assistant";
  content: string;
  image?: string;
}

const CHIPS = [
  { icon: Swords, text: "nmap арқылы торды қалай сканерлеймін?" },
  { icon: ShieldAlert, text: "Web-сайтты қалай қорғаймын? (OWASP Top 10)" },
  { icon: Bot, text: "Логтарды талдау: brute force белгілері" },
  { icon: ShieldAlert, text: "Phishing хаттан қалай қорғанадым?" },
];

export function AiAgentPage() {
  const { t } = useI18n();
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attach, setAttach] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<AbortController | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => () => streamRef.current?.abort(), []);

  const send = useCallback(
    async (text?: string) => {
      const content = (text ?? input).trim();
      if (!content || busy) return;
      setInput("");
      setError(null);
      const next: ChatMsg[] = [
        ...messages,
        { role: "user", content, image: attach ?? undefined },
      ];
      setMessages(next);
      setAttach(null);
      setBusy(true);

      // ассистент өрішін алдын ала аш (typing animation)
      const holder: ChatMsg[] = [...next, { role: "assistant", content: "" }];
      setMessages(holder);

      try {
        const res = await fetch("/api/backend/ai/chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken() ?? ""}`,
          },
          body: JSON.stringify({
            messages: next
              .slice(-12)
              .map((m) => ({ role: m.role, content: m.content, image: m.image })),
          }),
          signal: streamRef.current?.signal,
        });
        if (!res.ok || !res.body) {
          let msg = t("ai.error");
          try {
            const j = (await res.json()) as { message?: string; detail?: string };
            if (j.message) msg = j.message;
            if (j.detail) msg += ` (${j.detail.slice(0, 120)})`;
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        let got = false;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() ?? "";
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith("data:")) continue;
            const data = trimmed.slice(5).trim();
            if (data === "[DONE]") continue;
            try {
              const json = JSON.parse(data) as {
                choices?: { delta?: { content?: string } }[];
              };
              const delta = json.choices?.[0]?.delta?.content ?? "";
              if (delta) {
                got = true;
                setMessages((prev) => {
                  const copy = [...prev];
                  const last = copy[copy.length - 1];
                  copy[copy.length - 1] = { ...last, content: last.content + delta };
                  return copy;
                });
              }
            } catch {
              /* partial json — келесі chunk-пен жалғасады */
            }
          }
        }
        if (!got) {
          setMessages((prev) => prev.slice(0, -1));
          throw new Error(t("ai.empty"));
        }
      } catch (e) {
        const isAbort = e instanceof DOMException && e.name === "AbortError";
        if (!isAbort) {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === "assistant" && !last.content) return prev.slice(0, -1);
            return prev;
          });
          setError(e instanceof Error ? e.message : t("ai.error"));
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, input, messages, attach, t],
  );

  const pickImage = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Тек сурет файлын таңдаңыз (png/jpg/webp).");
      return;
    }
    if (file.size > 3.5 * 1024 * 1024) {
      setError("Сурет тым үлкен (макс. 3.5 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setAttach(String(reader.result));
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const reset = () => {
    streamRef.current?.abort();
    streamRef.current = null;
    setMessages([]);
    setAttach(null);
    setError(null);
  };

  return (
    <div className="page ai-page">
      <div className="page-heading">
        <h1>{t("ai.title")}</h1>
        <p className="muted">{t("ai.desc")}</p>
      </div>

      <div className="ai-card">
        <div className="ai-head">
          <div className="ai-avatar">
            <Bot size={22} />
            <span className="ai-avatar-pulse" aria-hidden />
          </div>
          <div>
            <strong>{t("ai.name")}</strong>
            <div className="ai-mode-row">
              <span className="badge badge-step-LAB_TASK">
                <Swords size={11} /> {t("ai.mode_red")}
              </span>
              <span className="badge badge-step-THEORY">
                <ShieldAlert size={11} /> {t("ai.mode_blue")}
              </span>
            </div>
          </div>
          <Button variant="ghost" onClick={reset} title={t("ai.reset")}>
            <RotateCcw size={15} /> {t("ai.reset")}
          </Button>
        </div>

        <div className="ai-scroll" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="ai-empty">
              <p>{t("ai.empty_hint")}</p>
              <div className="ai-chips">
                {CHIPS.map((c, i) => (
                  <button key={i} className="ai-chip" onClick={() => send(c.text)}>
                    <c.icon size={14} /> {c.text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => {
              const isAi = m.role === "assistant";
              const streaming = busy && isAi && i === messages.length - 1;
              return (
                <div key={i} className={`ai-msg ${isAi ? "ai-msg-ai" : "ai-msg-user"}`}>
                  {isAi && (
                    <div className="ai-msg-avatar">
                      <Bot size={15} />
                    </div>
                  )}
                  <div className={`ai-bubble ${streaming ? "streaming" : ""}`}>
                    {m.image && <img className="ai-img" src={m.image} alt="attachment" />}
                    {m.content ? (
                      <span className="ai-text">
                        {m.content}
                        {streaming && <span className="ai-caret" aria-hidden />}
                      </span>
                    ) : streaming ? (
                      <span className="ai-typing" aria-label="typing">
                        <i /><i /><i />
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {error && <div className="alert alert-error ai-error">{error}</div>}

        {attach && (
          <div className="ai-attach-row">
            <span className="ai-attach">
              <img src={attach} alt="" />
              {t("ai.attached")}
              <button onClick={() => setAttach(null)} aria-label={t("ai.remove_img")}>
                <X size={13} />
              </button>
            </span>
          </div>
        )}
        <div className="ai-input-row">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              pickImage(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            className="ai-attach-btn"
            onClick={() => fileRef.current?.click()}
            title={t("ai.attach_ph")}
            disabled={busy}
          >
            <ImagePlus size={18} />
          </button>
          <input
            className="input"
            value={input}
            placeholder={t("ai.input_ph")}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
            disabled={busy}
          />
          <Button onClick={() => send()} disabled={busy || (!input.trim() && !attach)}>
            <Send size={15} /> {t("ai.send")}
          </Button>
        </div>
        <p className="muted small ai-disc">{t("ai.disc")}</p>
      </div>
    </div>
  );
}
